import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Select, Skeleton, EmptyState, Pagination, useToast } from '@bbpos/ui';
import { formatCurrency } from '@/utils/currency';
import { toCurrencyCode } from '@/hooks/use-tenant-currency';
import { subscriptionService } from '@/services/subscription.service';
import type { InvoiceStatus, SubscriptionInvoice } from '@/types/subscription';
import {
  GLASS_PANEL,
  cn,
  formatINRDate,
  invoiceStatusTone,
  toneClasses,
} from './design-tokens';

const STATUS_OPTIONS: { value: '' | InvoiceStatus; label: string }[] = [
  { value: '', label: 'All statuses' },
  { value: 'PAID', label: 'Paid' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'FAILED', label: 'Failed' },
  { value: 'REFUNDED', label: 'Refunded' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

interface InvoiceHistoryPanelProps {
  tenantId: string;
  /** When true, action column (download) shows. */
  canDownload?: boolean;
  /** Visible heading override (e.g. "Invoice History" or "Your invoices"). */
  heading?: string;
  pageSize?: number;
}

function StatusPill({ status }: { status: InvoiceStatus }) {
  const palette = toneClasses(invoiceStatusTone(status));
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium',
        palette.pill,
      )}
    >
      <span className={cn('h-1.5 w-1.5 rounded-full', palette.dot)} />
      {status.toLowerCase()}
    </span>
  );
}

function InvoiceIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      className="h-5 w-5"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      className="h-4 w-4"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4"
      />
    </svg>
  );
}

function InvoiceRow({
  invoice,
  tenantId,
  canDownload,
}: {
  invoice: SubscriptionInvoice;
  tenantId: string;
  canDownload: boolean;
}) {
  // The invoice's own currency, not the viewer's (QA SS4-001).
  const currency = toCurrencyCode(invoice.currency);
  const [expanded, setExpanded] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const { toast } = useToast();

  async function handleDownload(e: React.MouseEvent) {
    e.stopPropagation();
    if (downloading) return;
    setDownloading(true);
    try {
      await subscriptionService.downloadInvoice(tenantId, invoice.id);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Download failed';
      toast({ title: message, variant: 'error' });
    } finally {
      setDownloading(false);
    }
  }
  return (
    <li
      className={cn(
        'rounded-xl px-4 py-3 transition-all duration-150 hover:bg-white dark:hover:bg-neutral-800/60',
        'bg-white/40 dark:bg-neutral-800/30',
      )}
    >
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="w-full text-left"
        aria-expanded={expanded}
      >
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[220px]">
            <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
              {invoice.plan_display_name}
            </p>
            <p className="mt-0.5 text-xs tabular-nums text-neutral-500 dark:text-neutral-400">
              {formatINRDate(invoice.period_start)} &rarr; {formatINRDate(invoice.period_end)}
            </p>
          </div>
          <div className="text-right min-w-[110px]">
            <p className="text-sm font-semibold tabular-nums text-neutral-900 dark:text-neutral-100">
              {formatCurrency(invoice.amount, currency)}
            </p>
            <p className="mt-0.5 text-[11px] uppercase tracking-wider text-neutral-400 dark:text-neutral-500">
              {invoice.currency || 'INR'}
            </p>
          </div>
          <div className="min-w-[90px]">
            <StatusPill status={invoice.status} />
          </div>
          <div className="min-w-[130px] text-right">
            <p className="text-xs tabular-nums text-neutral-500 dark:text-neutral-400">
              {formatINRDate(invoice.created_at)}
            </p>
          </div>
          {canDownload && (
            <button
              type="button"
              onClick={handleDownload}
              disabled={downloading}
              aria-label={`Download invoice ${invoice.id}`}
              className={cn(
                'ml-1 inline-flex h-8 w-8 items-center justify-center rounded-lg transition-all',
                'text-neutral-500 hover:bg-indigo-50 hover:text-indigo-600',
                'dark:text-neutral-400 dark:hover:bg-indigo-900/30 dark:hover:text-indigo-300',
                downloading && 'opacity-60',
              )}
            >
              <DownloadIcon />
            </button>
          )}
        </div>
      </button>

      {expanded && (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3 rounded-xl bg-white/60 dark:bg-neutral-900/40 p-3 text-xs animate-fade-in">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-neutral-400 dark:text-neutral-500">
              Payment method
            </p>
            <p className="mt-0.5 text-neutral-800 dark:text-neutral-200">
              {invoice.payment_method || '—'}
            </p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-neutral-400 dark:text-neutral-500">
              Reference
            </p>
            <p className="mt-0.5 font-mono text-[11px] text-neutral-800 dark:text-neutral-200 truncate">
              {invoice.payment_reference || '—'}
            </p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-neutral-400 dark:text-neutral-500">
              Invoice ID
            </p>
            <p className="mt-0.5 font-mono text-[11px] text-neutral-800 dark:text-neutral-200 truncate">
              {invoice.id}
            </p>
          </div>
          {invoice.notes && (
            <div className="sm:col-span-3">
              <p className="text-[10px] uppercase tracking-wider text-neutral-400 dark:text-neutral-500">
                Notes
              </p>
              <p className="mt-0.5 text-neutral-700 dark:text-neutral-300">{invoice.notes}</p>
            </div>
          )}
        </div>
      )}
    </li>
  );
}

export function InvoiceHistoryPanel({
  tenantId,
  canDownload = true,
  heading = 'Invoice history',
  pageSize = 10,
}: InvoiceHistoryPanelProps) {
  const [status, setStatus] = useState<'' | InvoiceStatus>('');
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['subscription', 'invoices', tenantId, status, page],
    queryFn: () =>
      subscriptionService.listInvoices(tenantId, {
        status: status || undefined,
        page,
        limit: pageSize,
      }),
    enabled: Boolean(tenantId),
  });

  const invoices = data?.data ?? [];
  const meta = data?.meta;

  return (
    <div className={cn(GLASS_PANEL, 'p-6')}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400">
            <InvoiceIcon />
          </div>
          <div>
            <h3 className="text-base font-semibold tracking-tight text-neutral-900 dark:text-neutral-100">
              {heading}
            </h3>
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              Every subscription charge, refund, and pending balance.
            </p>
          </div>
        </div>
        <div className="w-[200px]">
          <Select
            options={STATUS_OPTIONS}
            value={status}
            onChange={(v) => {
              setStatus(v as '' | InvoiceStatus);
              setPage(1);
            }}
          />
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={52} />
          ))}
        </div>
      ) : invoices.length === 0 ? (
        <EmptyState
          variant="section"
          title={status ? `No ${status.toLowerCase()} invoices` : 'No invoices yet'}
          description={
            status
              ? 'Try switching the status filter — older invoices may have a different status.'
              : 'Invoices appear here whenever the plan is upgraded, downgraded, or renewed.'
          }
          icon={
            <div className="h-12 w-12 rounded-2xl bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400 flex items-center justify-center">
              <InvoiceIcon />
            </div>
          }
        />
      ) : (
        <>
          <div
            className={cn(
              'mb-3 grid gap-3 px-4 text-[10px] uppercase tracking-[0.18em] text-neutral-400 dark:text-neutral-500',
              canDownload
                ? 'grid-cols-[1fr_110px_90px_130px_40px]'
                : 'grid-cols-[1fr_110px_90px_130px]',
            )}
          >
            <span>Plan &amp; period</span>
            <span className="text-right">Amount</span>
            <span>Status</span>
            <span className="text-right">Created</span>
            {canDownload && <span className="text-right">&nbsp;</span>}
          </div>
          <ul className="space-y-2">
            {invoices.map((invoice) => (
              <InvoiceRow
                key={invoice.id}
                invoice={invoice}
                tenantId={tenantId}
                canDownload={canDownload}
              />
            ))}
          </ul>
          {meta && meta.totalPages > 1 && (
            <div className="mt-4 flex justify-end">
              <Pagination
                currentPage={meta.page}
                totalPages={meta.totalPages}
                totalItems={meta.total}
                pageSize={meta.limit}
                onPageChange={setPage}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
